import { useCallback, useEffect } from 'react';
import { usePublisherStore } from '../store/models/publisher';
import { useSubscriptionsStore } from '../store/models/subscriptions';

// The S3-backed map overrides a publisher's own widgetParamsString when the
// pubId is listed there; otherwise fall back to what the publisher's own
// list endpoint (whats-hot/featured/etc.) already provided. Checked by
// presence (typeof === 'string'), not truthiness, so an explicit "" override
// (intentionally clearing a publisher's category) is honored rather than
// silently falling through.
function resolveWidgetParams(publisher, widgetParamsMap) {
    if (!publisher) return undefined;
    const entry = widgetParamsMap?.publishers?.[publisher.pubId];
    if (entry && typeof entry.widgetParamsString === 'string') return entry.widgetParamsString;
    return publisher.widgetParamsString;
}

// Publishers that don't want a sync-display at all (EBL-13184) — send the
// user to their own site instead of launching the widget.
function resolveNavigateToURL(publisher, widgetParamsMap) {
    return widgetParamsMap?.publishers?.[publisher?.pubId]?.navigateToURL;
}

// The widget script itself is injected per-environment from a *.ecal.com host
// (see initEnv.js's WIDGET_HOSTS: {testing,staging,''}sync.ecal.com) — a
// suffix check (rather than an exact host match) covers every environment
// without hardcoding a single subdomain that could drift or differ by env.
function isTrustedWidgetOrigin(origin) {
    try {
        const { hostname } = new URL(origin);
        return hostname === 'ecal.com' || hostname.endsWith('.ecal.com');
    } catch {
        return false;
    }
}

function useSync() {
    const publisherStore = usePublisherStore();
    const subscriptionsStore = useSubscriptionsStore();

    // The widget notifies its host page via postMessage when it closes
    // (success or cancel, no way to tell them apart yet) — refetch
    // subscriptions/collections then, waiting 1s first to give the backend a
    // moment to catch up. refreshSubscriptionsAfterDelay owns that timer on
    // the store itself, so it's not tied to this effect's lifecycle.
    useEffect(() => {
        function handleWidgetMessage(event) {
            if (!isTrustedWidgetOrigin(event.origin)) return;
            if (event.data?.action !== 'closeWidget') return;
            subscriptionsStore.refreshSubscriptionsAfterDelay();
        }
        window.addEventListener('message', handleWidgetMessage);
        return () => window.removeEventListener('message', handleWidgetMessage);
    }, [subscriptionsStore]);

    const launchSync = useCallback((publisher) => {
        if (!publisher) return;

        const navigateToURL = resolveNavigateToURL(publisher, publisherStore.widgetParamsMap);
        if (navigateToURL) {
            window.open(navigateToURL, '_blank', 'noopener,noreferrer');
            return;
        }

        const { widgetId, apiKey } = publisher;

        if (!widgetId || !apiKey) return;

        if (typeof EcalWidget !== 'undefined' && EcalWidget.launchWidgetWithAPIKey) {
            EcalWidget.launchWidgetWithAPIKey(widgetId, apiKey, {
                category: resolveWidgetParams(publisher, publisherStore.widgetParamsMap),
            });
        }
    }, [publisherStore]);

    return { launchSync };
}

export default useSync;
